import { useEffect, useState } from 'react';
import './AuthTypography.css';
import './NewPatientDataPage.css';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import { Header } from '../Header';
import { header5NewUserJSON } from './StartPage';
import { SidebarIcon } from '../components/SidebarIcon';
import { t, type TranslationKey } from '../i18n/translations';

interface PendingAppointmentFull {
  when?: string;
  duration?: number;
  practitionerIds?: string[];
  procedureIds?: (string | null)[];
  procedureName?: string;
}

interface Procedure {
  id: string;
  name: string;
  duration: number;
}

const initialFormData = {
  firstName: '',
  familyName: '',
  dateOfBirth: '',
  address: '',
  zipCode: '',
  town: '',
  email: '',
  mobile: '',
};

// Keep the form only in memory while the patient changes the treatment.
let treatmentChangeDraft: {
  formData: typeof initialFormData;
  acceptedDatenschutzerklaerung: boolean;
  acceptedTerms: boolean;
} | null = null;

const readPendingAppointmentFull = () => {
  try {
    const value = JSON.parse(localStorage.getItem('pendingAppointmentFull') ?? 'null');
    return value && typeof value === 'object' ? (value as PendingAppointmentFull) : null;
  } catch {
    return null;
  }
};

const readCachedProcedure = (procedureId: string) => {
  try {
    const cache = JSON.parse(localStorage.getItem('procedureCache') ?? '{}');
    return cache && typeof cache === 'object' ? (cache[procedureId] as Procedure | undefined) : undefined;
  } catch {
    return undefined;
  }
};

const translateProcedureName = (procedureName?: string) => {
  if (!procedureName) return 'Behandlung';
  const translatedName = t(procedureName as TranslationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const readAppointmentDate = (when?: string) => {
  if (!when) return null;
  const date = new Date(when);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatAppointmentDate = (date: Date) =>
  new Intl.DateTimeFormat('de-CH', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);

const formatAppointmentTime = (date: Date) =>
  `${new Intl.DateTimeFormat('de-CH', { hour: '2-digit', minute: '2-digit' }).format(date)} Uhr`;

export function NewPatientDataPage() {
  localStorage.setItem('headerDefinition', header5NewUserJSON);
  localStorage.setItem('headerPhase', '4');

  const pendingAppointment = readPendingAppointmentFull();
  const selectedProcedureId =
    pendingAppointment?.procedureIds?.[0]?.trim() || localStorage.getItem('selectedProcedureId')?.trim() || '';
  const selectedPractitionerId =
    pendingAppointment?.practitionerIds?.[0]?.trim() || localStorage.getItem('selectedPractitionerId')?.trim() || '';
  const cachedProcedure = selectedProcedureId ? readCachedProcedure(selectedProcedureId) : undefined;
  const appointmentName = translateProcedureName(pendingAppointment?.procedureName ?? cachedProcedure?.name);
  const appointmentDuration = cachedProcedure?.duration ?? pendingAppointment?.duration;
  const appointmentDate = readAppointmentDate(pendingAppointment?.when);
  const [practitionerName, setPractitionerName] = useState(selectedPractitionerId);

  console.log("NewPatientDataPage: selectedPractitionerId=" + selectedPractitionerId);

  const [formData, setFormData] = useState(() => treatmentChangeDraft?.formData ?? { ...initialFormData });
  const [acceptedDatenschutzerklaerung, setAcceptedDatenschutzerklaerung] = useState(
    () => treatmentChangeDraft?.acceptedDatenschutzerklaerung ?? false
  );
  const [acceptedTerms, setAcceptedTerms] = useState(() => treatmentChangeDraft?.acceptedTerms ?? false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    treatmentChangeDraft = null;
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadPractitioner = async () => {

      console.log("loadPractitioner: selectedPractitionerId=" + selectedPractitionerId);

      if (!selectedPractitionerId) {
        setPractitionerName('Behandler');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/practitioners/titled-name/${selectedPractitionerId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {

          console.log("loadPractitioner: bad response " + response.headers.get('content-type'));

          return;
        }

        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json') ? await response.json() : await response.text();
        const titledName =
          typeof payload === 'string'
            ? payload.trim()
            : typeof payload?.titledName === 'string'
              ? payload.titledName.trim()
              : typeof payload?.name === 'string'
                ? payload.name.trim()
                : typeof payload?.fullName === 'string'
                  ? payload.fullName.trim()
                  : '';

        console.log("loadPractitioner: payload=" + JSON.stringify(payload));

        if (!ignore) {
          setPractitionerName(titledName || selectedPractitionerId);
        }
      } catch {
        if (!ignore) setPractitionerName(selectedPractitionerId || 'Behandler');
      }
    };

    loadPractitioner();
    return () => {
      ignore = true;
    };
  }, [selectedPractitionerId]);

  const onDatenschutzerklaerungChange = () => {
    setAcceptedDatenschutzerklaerung((currentValue) => !currentValue);
  };

  const onTermsChange = () => {
    setAcceptedTerms((currentValue) => !currentValue);
  };

  const back = () => {
    navigate('/date-time-entry');
  };

  const changeTreatment = () => {
    treatmentChangeDraft = {
      formData: { ...formData },
      acceptedDatenschutzerklaerung,
      acceptedTerms,
    };
    localStorage.removeItem('pendingAppointmentFull');
    localStorage.removeItem('selectedPractitionerId');
    localStorage.removeItem('selectedProcedureId');
    localStorage.removeItem('selectedAppointmentId');
    navigate('/procedures');
  };

  const next = async () => {
    if (!canContinue || loading) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      // This flow deliberately omits a password. The backend must support
      // registration without one; never supply a placeholder credential.
      const response = await fetch(`${API_BASE_URL}/patients/register-full`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userName: formData.email.trim(),
          firstName: formData.firstName,
          familyName: formData.familyName,
          mobile: formData.mobile,
          email: formData.email.trim(),
          dateOfBirth: formData.dateOfBirth,
          zipCode: formData.zipCode,
          town: formData.town,
          addressLines: [formData.address],
          languages: ['de'], // TODO
          officeId: 'data_c_office_id', // TODO
          customerId: 'data_c_customer_id', // TODO
          // TODO: more possible request fields:
          //   extId
          //   salutation
          //   title
          //   phones
          //   patientType
          //   patientInfoMap
        }),
      });

      if (response.ok) {
        const patientId = (await response.text()).trim();

        console.log("patientId=" + patientId);

        if (!patientId) {
          throw new Error('Die Registrierung hat keine Patienten-ID zurückgegeben.');
        }

        localStorage.setItem('selectedPatientId', patientId);
        localStorage.setItem('loginPatientId', patientId);
        const pendingAppointmentFull = {
          when: pendingAppointment?.when,
          duration: appointmentDuration,
          practitionerIds: selectedPractitionerId ? [selectedPractitionerId] : [],
          procedureIds: selectedProcedureId ? [selectedProcedureId] : [],
          procedureName: pendingAppointment?.procedureName ?? cachedProcedure?.name,
        };
        localStorage.setItem('pendingAppointmentFull', JSON.stringify(pendingAppointmentFull));
        //navigate('/login');
        //navigate('/appointment-booked');
        //navigate('/registration-done');
        navigate('/appointment-summary');
        return;
      } else {
        console.log("/register-full failed (!response.ok):");
      }

      if (response.status === 409) {
        setError('Benutzer ist bereits registriert.');
        return;
      }

      setError(`Registrierung fehlgeschlagen: ${response.status} ${response.statusText}`);
    } catch (err) {
      setError(`Registrierung fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    } finally {
      setLoading(false);
    }
  };

  /* the request in /patients/register-full:
     * Request:
      userName
      firstName
      familyName
      email
      mobile
      customerId
      extId
      salutation
      title
      zipCode
      town
      dateOfBirth
      addressLines
      phones
      languages
      officeId
      patientType
      patientInfoMap
     * Creates a User, UserDetail and Patient. Returns the patientId.
     * TODO: currently checks for duplicate Users but little else,
     * should at least check for email format and legit mobile number.
     * Maybe also full name vs. just initials.
  */

  const onChange = (field: keyof typeof formData, value: string) => {
    setFormData((currentValue) => ({
      ...currentValue,
      [field]: value,
    }));
  };

  const requiredFieldsAreFilled =
    formData.firstName.trim() !== '' &&
    formData.familyName.trim() !== '' &&
    formData.address.trim() !== '' &&
    formData.zipCode.trim() !== '' &&
    formData.town.trim() !== '' &&
    formData.mobile.trim() !== '' &&
    formData.email.trim() !== '';

  const emailIsValid = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(formData.email.trim());
  const canContinue =
    requiredFieldsAreFilled &&
    emailIsValid &&
    acceptedDatenschutzerklaerung &&
    acceptedTerms;

  return (
    <div className="auth-page booking-new-patient min-h-screen bg-[#fbf9f5] text-slate-900">
      <Header compact />
      <div className="booking-new-patient__content mx-auto max-w-[1360px] px-6 py-12">
        <div className="booking-new-patient__layout grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="booking-new-patient__selection min-w-0 self-start rounded-3xl border border-[#efe7da] bg-white px-6 py-6 shadow-[0_14px_30px_rgba(19,19,19,0.04)] lg:col-start-1">
            <p className="text-sm font-semibold text-[#d0bb91]">Ihre Auswahl</p>

            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fbf8f1] text-[#c5b18d]">
                  <SidebarIcon name="document" className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="booking-selection__value break-words text-[18px] font-semibold leading-6 text-slate-800">{appointmentName}</p>
                  {appointmentDuration ? (
                    <p className="mt-0.5 text-[15px] font-medium leading-5 text-slate-400">
                      {appointmentDuration} Minuten
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fcfbf8] text-[#c5b18d]">
                  <SidebarIcon name="profile" className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium leading-5 text-slate-400">Behandler</p>
                  <p className="booking-selection__value break-words text-[18px] font-semibold leading-6 text-slate-800">
                    {practitionerName || 'Behandler'}
                  </p>
                </div>
              </div>

              {appointmentDate ? (
                <>
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fcfbf8] text-[#c5b18d]">
                      <SidebarIcon name="calendar" className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium leading-5 text-slate-400">Datum</p>
                      <p className="booking-selection__value break-words text-[18px] font-semibold leading-6 text-slate-800">
                        {formatAppointmentDate(appointmentDate)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fcfbf8] text-[#c5b18d]">
                      <SidebarIcon name="clock" className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium leading-5 text-slate-400">Uhrzeit</p>
                      <p className="booking-selection__value break-words text-[18px] font-semibold leading-6 text-slate-800">
                        {formatAppointmentTime(appointmentDate)}
                      </p>
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            <button
              type="button"
              onClick={changeTreatment}
              className="mt-8 inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-full border border-[#ebe7df] bg-white px-4 text-sm font-semibold text-slate-500 transition hover:border-[#d9c39b] hover:bg-[#fbf8f1] hover:text-slate-700"
            >
              Ändern
            </button>
          </aside>

          <main className="min-w-0 lg:col-start-2">
            <div className="booking-new-patient__form rounded-[28px] border border-[#efe7da] bg-white px-6 py-8 shadow-[0_18px_40px_rgba(19,19,19,0.05)] md:px-10 md:py-10">
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">
                  Persönliche Angaben eintragen
                </h1>
                <p className="mt-2 text-sm text-slate-400">
                  Bitte füllen Sie das Formular aus, um Ihre Terminbuchung abzuschliessen.
                </p>
              </div>

              <div className="booking-new-patient__fields mt-10 grid gap-x-6 gap-y-5 md:grid-cols-2 md:gap-y-6">
                <label className="block">
                  <span className="text-xs font-semibold text-slate-400">Vorname</span>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(event) => onChange('firstName', event.target.value)}
                    className="mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-semibold text-slate-400">Name</span>
                  <input
                    type="text"
                    value={formData.familyName}
                    onChange={(event) => onChange('familyName', event.target.value)}
                    className="mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-400">Geburtsdatum</span>
                  <input
                    type="text"
                    value={formData.dateOfBirth}
                    onChange={(event) => onChange('dateOfBirth', event.target.value)}
                    className="auth-numeric mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-400">Strasse und Hausnummer</span>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(event) => onChange('address', event.target.value)}
                    className="mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-semibold text-slate-400">Postleitzahl</span>
                  <input
                    type="text"
                    value={formData.zipCode}
                    onChange={(event) => onChange('zipCode', event.target.value)}
                    className="auth-numeric mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-semibold text-slate-400">Ort</span>
                  <input
                    type="text"
                    value={formData.town}
                    onChange={(event) => onChange('town', event.target.value)}
                    className="mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-400">E-Mail-Adresse</span>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={formData.email}
                    onChange={(event) => onChange('email', event.target.value)}
                    className="mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-semibold text-slate-400">Handynummer</span>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={(event) => onChange('mobile', event.target.value)}
                    className="mt-2 h-14 w-full rounded-2xl border border-[#efe7da] bg-white px-5 text-base text-slate-800 outline-none transition focus:border-[#c4a67b]"
                  />
                </label>
              </div>

              <div className="mt-8 space-y-4">
                <label className="flex items-start gap-3 text-xs leading-5 text-slate-400">
                  <input
                    type="checkbox"
                    checked={acceptedDatenschutzerklaerung}
                    onChange={onDatenschutzerklaerungChange}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border border-[#d8ccb9] text-[#c4a67b] focus:ring-[#c4a67b]"
                  />
                  <span>
                    Ich habe die <span className="font-semibold text-[#c4a67b]">Datenschutzerklärung</span> gelesen
                    und akzeptiere diese. Ich willige ein, dass meine Daten zum Zweck der Terminvereinbarung
                    verarbeitet werden.
                  </span>
                </label>

                <label className="flex items-start gap-3 text-xs leading-5 text-slate-400">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={onTermsChange}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border border-[#d8ccb9] text-[#c4a67b] focus:ring-[#c4a67b]"
                  />
                  <span>
                    Mit Ihrer Registrierung nehmen Sie zur Kenntnis, dass nicht wahrgenommene Termine oder Termine,
                    die nicht mind. 24 Stunden im Voraus abgesagt werden, in Rechnung gestellt werden können. Ergibt
                    die Bonitätsprüfung ein negatives Resultat, kann die Zahlung vor Ort verlangt werden.
                  </span>
                </label>
              </div>

              {error && (
                <div className="mt-6 text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>

            <div className="booking-new-patient__actions mt-6 flex flex-col items-stretch justify-end gap-3 sm:flex-row">
              <button
                type="button"
                onClick={back}
                className="inline-flex min-w-[145px] cursor-pointer items-center justify-center gap-2 rounded-2xl border border-[#d9c8ab] bg-white px-8 py-3 text-sm font-semibold text-slate-500 transition hover:border-[#d9c39b] hover:bg-[#fbf8f1]"
              >
                <span aria-hidden="true">←</span>
                <span>Zurück</span>
              </button>

              <button
                type="button"
                onClick={next}
                disabled={!canContinue || loading}
                className={`inline-flex min-w-[145px] items-center justify-center gap-2 rounded-2xl px-8 py-3 text-sm font-semibold text-white transition enabled:cursor-pointer enabled:hover:bg-[#bca477] ${canContinue && !loading
                  ? 'bg-[#cbb58a] shadow-[0_10px_20px_rgba(203,181,138,0.3)]'
                  : 'cursor-not-allowed bg-[#e6dccb] shadow-none'
                  }`}
              >
                <span>{loading ? 'Speichern...' : 'Weiter'}</span>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
